import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GAME_HAND, GAME_HAND_PHASE, PLAYER_STATUS } from 'constants/gameConstants';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppSelector } from 'hooks';
import type { WaitlistEntry } from 'types/waitlist';
import { getCardValue, isWinner } from 'utils/functions';
import { readLastOpenTables, RESTORE_MAX_WAIT_MS } from 'utils/lastOpenTables';
import {
  isExitedTable,
  isPlayerExitedTable,
  nextRecentlyExitedExpiry,
  RECENTLY_EXITED_GRACE_MS
} from 'utils/recentlyExitedTables';
import {
  addTableSlotsInUse,
  countHeldTableSlots,
  countUsedTableSlots,
  isQueuedSpot,
  type PendingSng,
  publishShownTableSlots,
  queuedGameIdsOf,
  queuedSlotIdOf,
  shownTableSlotsOf,
  withdrawShownTableSlots
} from 'utils/tableCapacity';
import { isValidTournamentId } from 'utils/tournamentSubscriptions';

import { isSeatPrompted } from 'components/UserActions/MultiRunActions/multiRunPhase';
import { voteFromSeatAction } from 'components/UserActions/MultiRunActions/voteFromAction';
import { voteWindowSecs } from 'components/UserActions/MultiRunActions/voteWindow';

import { isShutdownPending } from './shutdownState';
import { formatStakesLabel } from './stakesLabel';
import { arrangeInTableOrder } from './tableOrder';
import { ActiveTable, Rank, Suit } from './types';

// ---------------------------------------------------------------------------
// Shared "Your Tables" derivation.
//
// Both the lobby (SaloonLobbyContainer) and the in-table header (TableHeader)
// need the SAME notion of "the player's open tables" — same hole cards, same
// seated/observing/folded state, same left→right order — so the YourTables fan
// reads identically wherever it appears. This hook is the single source of that
// derivation; previously it lived inline in SaloonLobbyContainer.
// ---------------------------------------------------------------------------

const RANKS: Rank[] = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'];

const SUIT_SYMBOL: Record<string, Suit> = { s: '♠', h: '♥', d: '♦', c: '♣' };

// Normalize a rank string ('A','K',…,'10', or 'T' for ten) → our Rank, and a
// suit ('s'|'h'|'d'|'c') → pip symbol. Returns null for anything unrecognized.
const toDisplayCard = (rankRaw: string, suitRaw: string): { rank: Rank; suit: Suit } | null => {
  const rankStr = rankRaw.toUpperCase() === 'T' ? '10' : rankRaw.toUpperCase();
  const rank: Rank | undefined = (RANKS as readonly string[]).includes(rankStr)
    ? (rankStr as Rank)
    : undefined;
  const suit = SUIT_SYMBOL[suitRaw.toLowerCase()];
  if (!rank || !suit) return null;
  return { rank, suit };
};

// PRIMARY source: the live per-table game payload in state.tables. The engine
// ships each hole card as a string on game.p{seatId}cardN — rank is every char
// except the last, suit is the last char ('Ah', 'Kd', '10s'), exactly how the
// in-table Seats/Card components read them. Parsed here so YourTables shows the
// same cards as the table itself, and updates atomically with the rest of the
// table state (no cross-source flicker). Returns null when observing / between
// hands (no card fields) → the strip renders placeholder card backs.
const parseEngineCard = (raw: any): { rank: Rank; suit: Suit } | null => {
  if (typeof raw !== 'string' || raw.length < 2) return null;
  return toDisplayCard(raw.substring(0, raw.length - 1), raw.charAt(raw.length - 1));
};
const cardsFromTable = (game: any, seatId: number): { rank: Rank; suit: Suit }[] | null => {
  if (!game || seatId <= 0) return null;
  const parsed = [1, 2, 3, 4, 5]
    .map((i) => game[`p${seatId}card${i}`])
    .filter((raw: unknown): raw is string => typeof raw === 'string' && raw.length >= 2)
    // Highest rank first — the SAME order the felt lays the player's own hole
    // cards out in (components/Table/Seats.tsx sorts the bottom seat by
    // getCardValue descending). The engine ships them in dealing order, so
    // without this the preview showed "9 Q" while the table showed "Q 9" for
    // the very same hand.
    .sort((a: string, b: string) => getCardValue(b) - getCardValue(a))
    .map(parseEngineCard)
    .filter((c): c is { rank: Rank; suit: Suit } => c !== null);
  return parsed.length >= 2 ? parsed : null;
};

// Turn clock for the player's-action preview ring — mirrors SeatTimer's inputs:
//   target = game.speed (total turn seconds)
//   elapsed = game.time - game.lastmove (server clock since the turn started)
// Returns null when any field is missing/invalid so the preview falls back to a
// plain pulsing ring rather than a broken countdown.
// Map a seat-status code to the short away-state label shown on the preview
// (mirrors the in-table Player status labels). Active / unmapped → null.
const SEAT_STATUS_LABELS: Record<number, string> = {
  [PLAYER_STATUS.SITTING_OUT]: 'SITTING OUT',
  [PLAYER_STATUS.FOR_SITOUT]: 'SITTING OUT',
  [PLAYER_STATUS.SHOW_CARDS_FOR_SITOUT]: 'SITTING OUT',
  [PLAYER_STATUS.LEAVING]: 'STANDING UP',
  [PLAYER_STATUS.POST_BLIND]: 'JOINING',
  [PLAYER_STATUS.WAIT_FOR_BB]: 'JOINING',
  [PLAYER_STATUS.EXIT_TABLE]: 'LEFT GAME',
  [PLAYER_STATUS.EXIT_TABLE_WAIT_FOR_BB]: 'LEFT GAME',
  [PLAYER_STATUS.EXIT_TABLE_POST_BLIND]: 'LEFT GAME'
};

export const seatStatusLabel = (statusCode: unknown): string | null => {
  const code = Number(statusCode);
  if (!Number.isFinite(code)) return null;
  return SEAT_STATUS_LABELS[code] ?? null;
};

/**
 * A countdown anchored to the server's own clock: `game.time - game.lastmove`
 * elapsed out of `totalSeconds`.
 *
 * The anchor is shared by the two windows the engine runs off `lastmove` — the
 * seat's turn clock (`game.speed`) and the multi-run vote window
 * (`game.multiRunVoteSecs`) — so they are one derivation with two totals rather
 * than two derivations that could disagree about what "elapsed" means.
 */
const buildServerClock = (
  game: any,
  totalSeconds: number
): { totalSeconds: number; elapsedSeconds: number } | null => {
  const current = Number(game?.time);
  const start = Number(game?.lastmove);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return null;
  if (!Number.isFinite(current) || !Number.isFinite(start)) return null;
  const elapsed = Math.max(0, Math.min(current - start, totalSeconds));
  return { totalSeconds, elapsedSeconds: elapsed };
};

export const buildTurnClock = (
  game: any
): { totalSeconds: number; elapsedSeconds: number } | null =>
  buildServerClock(game, Number(game?.speed));

/**
 * The multi-run vote window as a countdown, for a table the player is NOT
 * necessarily looking at (EN-10237).
 *
 * `owesVote` is the caller's business — this only turns the window into a
 * clock, and returns null when there is no window to count.
 */
export const buildVoteClock = (
  game: any
): { totalSeconds: number; elapsedSeconds: number } | null =>
  buildServerClock(game, voteWindowSecs(game));

// FALLBACK source (Pass 2 only): state.playerGames[].card1..card5 are already
// { rank, suit } objects. Used for tables the engine lists for the player but
// that don't have a state.tables game payload yet.
const cardsFromPlayerGame = (pg: any): { rank: Rank; suit: Suit }[] | null => {
  if (!pg) return null;
  const parsed = [pg.card1, pg.card2, pg.card3, pg.card4, pg.card5]
    .map((c) => (c && c.rank && c.suit ? toDisplayCard(String(c.rank), String(c.suit)) : null))
    .filter((c): c is { rank: Rank; suit: Suit } => c !== null)
    // Highest first, as above — RANKS runs A→2, so ascending index IS
    // descending rank.
    .sort((a, b) => RANKS.indexOf(a.rank) - RANKS.indexOf(b.rank));
  return parsed.length >= 2 ? parsed : null;
};

/**
 * A tournamentId in the one form every order key uses ('0777' and 777 are
 * both '777'), or undefined when it isn't a positive integer. Everything that
 * builds a `tournament:<id>` key goes through this, so a bad value ('null',
 * -1, 'abc') can never become a key that unrelated tables share.
 */
const canonicalTournamentId = (raw: unknown): string | undefined =>
  isValidTournamentId(raw) ? String(Number(raw)) : undefined;

const isTournamentTabletype = (tabletype: unknown): boolean =>
  tabletype === 't' || tabletype === 'm';

/**
 * A tournamentId that is present but not usable ('null', -1, 'abc'), as
 * opposed to simply missing (playerGames rows never carry one; cash tables
 * default to ''). A table with an invalid id falls back to a game key — its
 * gameID — instead of waiting for an id that will never be valid.
 */
const hasInvalidTournamentId = (raw: unknown): boolean => {
  if (raw === undefined || raw === null) return false;
  const text = String(raw).trim();
  return text !== '' && text !== '0' && !isValidTournamentId(raw);
};

// A tournament table's id, from its own payload or — when the payload lacks
// it (got_playerGames rows never carry one) — from the player's tournament
// details. Undefined for cash tables, or when neither source has a valid id.
const resolveTournamentId = (
  tabletype: ActiveTable['tabletype'],
  raw: unknown,
  known: string | undefined
): string | undefined => {
  if (!isTournamentTabletype(tabletype)) return undefined;
  return canonicalTournamentId(raw) ?? known;
};

// Build the YourTables strip from BOTH state.tables (live subscriptions,
// includes observed-but-not-seated tables) AND state.playerGames (covers
// the brief gap where the server knows the player is seated but the first
// GOT_TEXAS hasn't populated state.tables yet). Dedupe by gameID; subscription
// data wins because it carries the live `game` payload with real cards.
/**
 * Whether a vote window has run out on the LOCAL clock, regardless of what the
 * last payload said.
 *
 * Asked once per table per render with the window's server-side facts; answers
 * from a deadline the hook fixed when it first saw that window. Defaults to
 * "not expired", which reproduces the payload-only behaviour exactly.
 */
export type VoteWindowExpired = (
  gameID: string,
  window: { anchor: unknown; totalSeconds: number; elapsedSeconds: number }
) => boolean;

export const adaptActiveTables = (
  playerGames: any[],
  tables: Record<string, any>,
  isExcluded: (gameID: string) => boolean = isExitedTable,
  voteWindowExpired: VoteWindowExpired = () => false,
  tournamentIdByGameID: Record<string, string> = {}
): ActiveTable[] => {
  const out: ActiveTable[] = [];
  const seen = new Set<string>();

  // Pass 1: live subscriptions in state.tables
  for (const [gid, table] of Object.entries(tables || {})) {
    const gameID = String(gid);
    if (!table?.game?.gameID || table.game.gameID === 0) continue;
    // Suppress a table the player just left. After a mid-hand exit, an
    // in-flight GOT_TEXAS/GOT_OMAHA can briefly re-add the slice (or it lingers
    // in state.playerGames below) before the engine catches up — which made the
    // "Your Tables" card flicker between its live mid-hand state and gone. The
    // recently-exited grace window (same guard App.tsx uses for broadcasts)
    // keeps it removed cleanly until the server state settles.
    if (isExcluded(gameID)) continue;
    seen.add(gameID);

    const game: ActiveTable['game'] = table.game.game === 'omaha' ? 'omaha' : 'texas';
    const tabletype: ActiveTable['tabletype'] = (table.game.tabletype ||
      's') as ActiveTable['tabletype'];
    const seatId = Number(table?.currentPlayer?.seatId ?? -1);
    // Seated state and cards both come from THIS table's slice so they can't
    // desync (the flicker bug was cards from playerGames + seated from
    // state.tables racing). Observed-only tables have seatId <= 0 → not seated
    // → placeholder hand.
    const isSeated = seatId > 0;
    const handPhase = Number(table?.game?.hand);
    const isHandIdle =
      !isSeated ||
      (Number.isFinite(handPhase) &&
        (handPhase < GAME_HAND_PHASE.DEAL_CARDS ||
          handPhase >= GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND));
    const isFolded = !isHandIdle && Boolean(table?.currentPlayer?.isFolded);
    // Same derivation the felt uses for the winner pill, so the dock tile and
    // the table can never disagree about who won.
    const handOverNow = handPhase >= GAME_HAND.AFTER_RIVER_BETTING_ROUND;
    // A hand is LIVE from the deal through the river. Used to clear the tile's
    // latched outcome, so it never carries into the next hand.
    const isHandLive = handPhase >= GAME_HAND_PHASE.DEAL_CARDS && !handOverNow;

    // All in — same derivation the felt's seat badge uses (game[pNaction]), so
    // the tile and the pod cannot disagree. The straddle-all-in case reads as
    // 'straddle' with an empty stack.
    const seatAction = String((table?.game as any)?.[`p${seatId}action`] ?? '').toLowerCase();
    const seatStack = Number((table?.game as any)?.[`p${seatId}stack`] ?? NaN);
    const isTableAllIn =
      !isHandIdle &&
      seatId > 0 &&
      !isFolded &&
      (seatAction === 'allin' || (seatAction === 'straddle' && seatStack === 0));

    const tournamentId = resolveTournamentId(
      tabletype,
      (table?.game as any)?.tournamentId,
      tournamentIdByGameID[gameID]
    );

    // isWinner reads showdown.currentPot and then indexes pots with it, so it
    // throws on a payload carrying one without the other — which the engine
    // does emit mid-resolution. A throw here would take out the whole strip,
    // not just this tile, so both are checked and the call is fenced.
    const showdownData = table?.game?.showdown;
    const potsData = table?.game?.pots;
    const canJudgeWinner = seatId > 0 && Boolean(showdownData) && Boolean(potsData);
    let isTableWinner = false;
    // Showdown survives into the next hand. Only judge a win after this hand
    // ends, or the previous WINNER badge immediately latches again.
    if (canJudgeWinner && handOverNow && !isHandIdle) {
      try {
        isTableWinner = isWinner(potsData, showdownData, seatId);
      } catch {
        // Half-formed payload — not a winner until it says so cleanly.
        isTableWinner = false;
      }
    }
    // "Your turn" only counts as actionable when the seat clock would actually
    // be running — mirror SeatTimer's gate: it's your move, the betting round is
    // live (startTurn, hand in 5..11), and no multi-run is in progress. Without
    // this, a stalled hand (e.g. every opponent sitting out) still showed the
    // turn timer/ACT counting down even though no action is possible.
    const handNum = Number(table?.game?.hand);
    // Lost at showdown: dealt in, did not fold, the showdown resolved, and the
    // pots went elsewhere. Gated on `showdown` EXISTING rather than on the hand
    // simply being over — the hand is "over" for a beat while the engine is
    // still deciding, and latching a loss in that window would show a loss on a
    // hand about to be won.
    // `handOver` is load-bearing, not belt-and-braces. `showdown` is NOT cleared
    // when the next hand starts, so without it every new hand began as a
    // showdown loss: seated, not folded, stale showdown still on the slice, not
    // the winner. That latched instantly, which suppressed ALL IN, showed no
    // badge (a loss carries none), and froze the cards at null — so the tile sat
    // on face-down backs for the whole hand while the felt showed a live one.
    const isShowdownLoss =
      !isHandIdle && seatId > 0 && !isFolded && canJudgeWinner && handOverNow && !isTableWinner;
    const isLiveBettingTurn =
      Boolean(table?.currentPlayer?.isMyTurn) &&
      Boolean(table?.gameState?.startTurn) &&
      handNum > 4 &&
      handNum < 12 &&
      !table?.currentPlayer?.isMultiRunActive;
    const isMyTurn = isLiveBettingTurn;

    // EN-10237: this table is waiting on the PLAYER'S OWN multi-run vote.
    //
    // `isSeatPrompted` is the SHARED gate UserActions mounts the panel on, not a
    // local re-derivation — if the two disagree, a fan advertises a vote the
    // table would not actually prompt for, and a phantom vote outranks every
    // turn in the multi-table scroll queue. It was a local copy of the six
    // clauses once; the money half then got retrofitted to the prompt alone, and
    // every sitting-out seat (statuses 1/2/6/12 all read as neither waiting nor
    // folded) was advertised a vote no panel would render.
    //
    // Under the two-phase offer (EN-663 AC-2.5) the same rule carries the new
    // half for free: a responder is not nagged across tables while the initiator
    // is still deciding, because at that point the table is not waiting on them.
    // (`isFolded` and `isWaiting` here are the very fields the prompt reads:
    // currentTable derives both from `p{seat}bet` / the seat status, so those are
    // the same rule too, not a second opinion.)
    // The last clause is "has not answered yet", read off `p{seat}action` (which
    // reaches us as `players[seat].action`) rather than the React state the
    // prompt itself uses: that state belongs to the table's own subtree, and
    // the whole point here is to speak for a table the player is NOT looking
    // at. It is also the durable record, so this survives a reconnect for free
    // (the EN-10236 path).
    const owesMultiRunVote =
      isSeatPrompted(table?.game, seatId) &&
      Number(table?.gameState?.noPlayers) > 1 &&
      isSeated &&
      !table?.currentPlayer?.isWaiting &&
      !isFolded &&
      voteFromSeatAction(table?.players?.[seatId]?.action) === '0';

    // EXPIRY, client-side — the cross-table nag must withdraw itself.
    //
    // `isSeatPrompted` asks only whether `runs` still carries its 'v'. The
    // processor clears that on its next tick, so normally the flag goes away on
    // its own; but if THIS table's broadcast subscription drops or its tick
    // errors, the last payload keeps the 'v' indefinitely and nothing re-checks.
    //
    // That is not a cosmetic stall. `actionQueueHead` lets a voting table jump
    // the queue unconditionally, so one stuck table would own the grid's
    // auto-scroll for the rest of the session and no other table's turn clock
    // would ever be scrolled into view again — missed turns on real-money
    // tables. It also pins a red ring at 0 and, because the tile's stamp slot is
    // claimed by 'vote', suppresses that table's genuine ALL IN / WINNER / FOLD
    // state.
    //
    // MultiRunActions already withdraws the panel on `hasExpired &&
    // actionPathStatus === 'ok'`. Mirroring it here means the two surfaces agree
    // about whether a window is still open, and every consumer is fixed at once:
    // presence of `voteClock` is what the grid queue, the tile ring and the
    // table switcher all key off.
    // TWO expiries, because the payload can only answer one of them.
    //
    // `elapsedSeconds` is `game.time - game.lastmove`, both read off the frame —
    // which is exactly what a dropped subscription freezes. It settles "the
    // server says this window is over"; it cannot settle "no frame has arrived
    // for longer than the window lasts", because the quantity it is built from
    // stops moving at the same moment the frames do.
    //
    // So the second question is asked of a deadline the hook fixes ONCE, when it
    // first sees the window: local now + whatever the server said was left. Not
    // a wall-clock reading substituted for the server's — the two clocks
    // disagree, which is why `buildServerClock` floors elapsed at 0 — but an
    // interval measured entirely locally from a starting point the server gave.
    // That is skew-free, and it does not care how often frames arrive or whether
    // `game.time` (whole seconds) happens to repeat between them.
    const pendingVoteClock = owesMultiRunVote ? buildVoteClock(table?.game) : null;
    const voteClock =
      pendingVoteClock &&
      pendingVoteClock.elapsedSeconds < pendingVoteClock.totalSeconds &&
      !voteWindowExpired(gameID, {
        // `lastmove` anchors the window server-side and holds still for its
        // duration, so it identifies WHICH window without a token of its own.
        anchor: table?.game?.lastmove,
        totalSeconds: pendingVoteClock.totalSeconds,
        elapsedSeconds: pendingVoteClock.elapsedSeconds
      })
        ? pendingVoteClock
        : null;

    // Cards from the live per-table game payload — same source the table UI
    // renders. Null when observing / between hands → placeholder card backs.
    const cards: ActiveTable['cards'] = isHandIdle ? null : cardsFromTable(table?.game, seatId);

    out.push({
      id: gameID,
      gameID,
      game,
      tabletype,
      label: game === 'omaha' ? 'PLO' : "Hold'Em",
      // Through the label, like every other lobby surface: read raw, the
      // Current Tables fan's tooltip and aria-label said "$1.00/$2.00" while
      // the tile and its stake heading said "$1.00 / $2.00" — one lobby, two
      // spellings, and a screen reader hears two different tables.
      stakes: formatStakesLabel(table?.game?.stakes || ''),
      isMyTurn,
      isSeated,
      isFolded,
      isWinner: isTableWinner,
      isAllIn: isTableAllIn,
      isShowdownLoss,
      isHandLive,
      isHandIdle,
      handNo: String((table?.game as any)?.gameNo ?? ''),
      tournamentId,
      cards,
      // Only carry the countdown for the table actually awaiting the action.
      turnClock: isMyTurn ? buildTurnClock(table?.game) : null,
      // Same rule for the vote window: carried only while this table is
      // actually waiting on this player AND the window is still open, so a
      // consumer can treat its presence as the signal and never has to
      // re-derive the condition. See the expiry note above.
      voteClock,
      // The seated player's away-state (sitting out / standing up / etc.).
      statusLabel: isSeated ? seatStatusLabel(table?.game?.[`p${seatId}status`]) : null,
      // Hand finished / in showdown-payout (>= AFTER_RIVER) — e.g. everyone
      // folded, so there are no cards to show.
      handOver: handNum >= GAME_HAND.AFTER_RIVER_BETTING_ROUND,
      pendingShutdown: isShutdownPending(table?.game)
    });
  }

  // Pass 2: playerGames entries not yet in state.tables. These represent
  // seated tables where the engine has acknowledged the player but no
  // GOT_TEXAS payload has arrived yet. Cards still come straight from the
  // playerGames entry, so the hand shows even before state.tables catches up.
  for (const g of playerGames || []) {
    if (!g || !g.gameID) continue;
    const gameID = String(g.gameID);
    if (seen.has(gameID)) continue;
    // Same exit guard as Pass 1: a just-left table can linger in playerGames
    // until the engine pushes a fresh list — don't resurrect its card.
    if (isExcluded(gameID)) continue;
    // Skip the empty `[{}]` sentinel that state.playerGames sometimes carries.
    if (Object.keys(g).length <= 1) continue;
    seen.add(gameID);

    const game: ActiveTable['game'] = g.game === 'omaha' ? 'omaha' : 'texas';
    const tabletype = (g.tabletype || 's') as ActiveTable['tabletype'];
    const tournamentId = resolveTournamentId(
      tabletype,
      g.tournamentId,
      tournamentIdByGameID[gameID]
    );
    out.push({
      id: gameID,
      gameID,
      game,
      tabletype,
      label: game === 'omaha' ? 'PLO' : "Hold'Em",
      stakes: formatStakesLabel(g.stakes || ''),
      isMyTurn: g.myTurn === 1,
      // We're in playerGames, so we're seated. Folded mirrors PlayerTables'
      // check (bet string starting with the fold prefix).
      isSeated: true,
      isFolded: typeof g.bet === 'string' && g.bet.charAt(0) === 'F',
      // No slice yet, so no pots/showdown/action to judge these from.
      isWinner: false,
      isAllIn: false,
      isShowdownLoss: false,
      isHandLive: false,
      awaitingPayload: true,
      handNo: String((g as any)?.gameNo ?? ''),
      tournamentId,
      cards: cardsFromPlayerGame(g),
      pendingShutdown: isShutdownPending(g)
    });
  }

  return out;
};

/**
 * One table per tournament the player is in: their seat. A player sits at one
 * table of a tournament at a time, but more can be loaded at once — the old
 * table lingering after a rebalance (seated or not), or a stale playerGames
 * row.
 *
 * Kept, in order: the destination of a rebalance in flight once it is
 * loaded (`destinations`, tournamentId → gameID) — the details can still
 * assign the seat being left, and the new table wins as soon as it is there —
 * then the table the player's tournament details assign
 * (`assigned`, gameID → tournamentId, status 'P'), else the first listed
 * table the player is seated at, else — for a tournament the player is
 * playing (`playing`, its order keys) — the first listed. A player playing a
 * tournament can't observe its other tables (EN-9594), so none of them is
 * listed beside the seat or shares its slot.
 *
 * Every other table is observed and passes through: a busted or unregistered
 * player may watch several tables of a tournament. Tables with no
 * tournamentId (cash, or a tournament not known yet) pass through too.
 * Returns the input when nothing is dropped.
 */
export const oneTablePerTournament = (
  tables: ActiveTable[],
  assigned: Record<string, string>,
  playing: Set<string> = new Set(),
  destinations: Record<string, string> = {}
): ActiveTable[] => {
  const rank = (t: ActiveTable): number => {
    if (t.tournamentId && destinations[t.tournamentId] === t.gameID) return 3;
    if (assigned[t.gameID] === t.tournamentId) return 2;
    return t.isSeated ? 1 : 0;
  };
  const isObserved = (t: ActiveTable): boolean => rank(t) === 0 && !playing.has(orderKeyOf(t));
  const kept = new Map<string, ActiveTable>();
  for (const t of tables) {
    if (!t.tournamentId || isObserved(t)) continue;
    const current = kept.get(t.tournamentId);
    if (!current || rank(t) > rank(current)) kept.set(t.tournamentId, t);
  }
  const filtered = tables.filter(
    (t) => !t.tournamentId || isObserved(t) || kept.get(t.tournamentId) === t
  );
  return filtered.length === tables.length ? tables : filtered;
};

/**
 * The nav's entry for a table the player is queued on whose table isn't loaded
 * yet (joined from the lobby, reconnecting, maintenance). It holds the table's
 * slot so the nav shows it in place like any other table; YourTables renders
 * its WAIT / JOIN card there. Waitlists are cash tables, keyed by gameID;
 * the card's id is the spot's slot id (queuedSlotIdOf), distinct from a live
 * table of the other game with the same gameID.
 */
const waitlistPlaceholder = (spot: WaitlistEntry, slotId: string): ActiveTable => {
  const game: ActiveTable['game'] = spot.gameType === 'omaha' ? 'omaha' : 'texas';
  return {
    id: slotId,
    gameID: String(spot.gameId),
    game,
    tabletype: 's',
    label: game === 'omaha' ? 'PLO' : "Hold'Em",
    stakes: '',
    isMyTurn: false,
    isSeated: false,
    isFolded: false,
    isWinner: false,
    isAllIn: false,
    isShowdownLoss: false,
    isHandLive: false,
    isHandIdle: true,
    handNo: '',
    cards: null
  };
};

/**
 * The nav's entry for a Sit & Go the player is registered for that hasn't
 * started (EN-11131). The registration holds a table slot — the table cap
 * counts it — so the strip shows it too, rather than letting the cap refuse a
 * join while the strip shows a free slot. Keyed by the table the registration
 * reserves (see countUsedTableSlots), which is what opening it opens — as the
 * game the engine sends for it (`game` on the lobby row: what subscribeGame
 * looks the table up by), else 'texas' — the table the engine creates
 * tournament tables in — as the other tournament open paths (Join Table in
 * the tournament modals) do. When only the tournament is known (no
 * gameID from the lobby or the details), the card holds the slot under a
 * registration id (registrationSlotId) and can't be opened: there is no table.
 */
const sngRegistrationPlaceholder = (sng: PendingSng, sitAndGoList: unknown): ActiveTable => {
  const tournamentId = canonicalTournamentId(sng.tournamentId);
  const entry = (Array.isArray(sitAndGoList) ? sitAndGoList : []).find(
    (s: any) =>
      tournamentId !== undefined && canonicalTournamentId(s?.tournamentId ?? s?.id) === tournamentId
  );
  return {
    id: sng.gameID,
    gameID: sng.gameID,
    game: entry?.game === 'omaha' ? 'omaha' : 'texas',
    tabletype: 't',
    label: 'Sit & Go',
    stakes: '',
    isMyTurn: false,
    isSeated: false,
    isFolded: false,
    isWinner: false,
    isAllIn: false,
    isShowdownLoss: false,
    isHandLive: false,
    isHandIdle: true,
    handNo: '',
    tournamentId,
    cards: null,
    statusLabel: 'REGISTERED',
    notOpenable: !sng.tableKnown
  };
};

// ---------------------------------------------------------------------------
// Table order (EN-10545, EN-10857).
//
// A table enters the order when the player opens it (recordTableOpen) or when
// it first appears live, and every surface (dock, strip, grid, carousel)
// shows the same left→right order.
// The order is kept at module scope so all hook instances share one list, and
// persisted to localStorage so it survives remounts and refreshes.
//
// A closed table leaves a vacancy in its place that remembers which table
// left it. A table that comes back takes its own vacancy back; a new table —
// opened by the player or arriving on its own — takes the leftmost vacancy:
//
//   A | B | C | D  → B leaves →  A | · | C | D  → open N →  A | N | C | D
//   A | B | C | D  → C, then D leave → A | B | · | ·  → open N, M → A | B | N | M
//
// Only with no vacancy left does a new table go to the front (newest first).
// Exiting every table clears the order, so the next session starts fresh.
// ---------------------------------------------------------------------------
const SUBSCRIPTION_ORDER_KEY = 'subscribedTablesOrder';

// Who the stored order belongs to. clearAuth removes the order on logout, but
// if that removal failed, or the identity switched in place without a reload,
// another player's vacancies would decide where this player's tables land.
const SUBSCRIPTION_ORDER_OWNER_KEY = 'subscribedTablesOrderOwner';

/**
 * A closed table's slot, waiting for the next table. Never a real key. Stored
 * as `~vacant:<key>`, the key of the table that left it; a bare `~vacant`
 * (older builds) was left by no known table.
 */
export const VACANT_ORDER_SLOT = '~vacant';

const isVacant = (id: string): boolean =>
  id === VACANT_ORDER_SLOT || id.startsWith(`${VACANT_ORDER_SLOT}:`);

// The key of the table that left a vacancy ('' when unknown).
const vacatedBy = (id: string): string =>
  id === VACANT_ORDER_SLOT ? '' : id.slice(VACANT_ORDER_SLOT.length + 1);

const vacate = (key: string): string => `${VACANT_ORDER_SLOT}:${key}`;

let subscriptionOrder: string[] | null = null;
let subscriptionOrderOwner = '';
// What was last written to storage, so an unchanged order isn't rewritten on
// every broadcast.
let lastWrittenOrder = '';

// Tables open before this page load come back one at a time, so until each
// one is back, "not loaded" does not mean "closed". Each is protected until it
// is seen live (removed from `gameIDs`; closing it then frees its slot) or the
// restore window ends (it closed while the player was away). Keyed by gameID,
// so a remembered tournament table, tracked under its tournament key, holds
// every tournament slot until it loads (see tournamentRestorePending).
let bootRestore: {
  gameIDs: Record<string, string>;
  until: number;
  settledAt: number;
} | null = null;

// A refresh is not an exit. After a page load, tables drop out and come back
// while they resubscribe, and the table the player was on stays hidden for the
// recently-exited grace (the page's pagehide leave marks it). Until both have
// passed, tables going missing says nothing about the player leaving them.
const POST_LOAD_SETTLE_MS = Math.max(RESTORE_MAX_WAIT_MS, RECENTLY_EXITED_GRACE_MS);

// Nothing is pruned while nothing is loaded, so the last tables to close keep
// their keys. These are the ones the prune would have vacated: an explicit
// open vacates them first, so it fills the lobby from the left instead of
// skipping past them.
let closedWhileIdle = new Set<string>();

// Turn the tables that closed while nothing was loaded into the vacancies the
// prune would have made, before a new table is placed.
const vacateClosedWhileIdle = (order: string[]): string[] => {
  if (closedWhileIdle.size === 0) return order;
  const vacated = order.map((id) => (closedWhileIdle.has(id) ? vacate(id) : id));
  closedWhileIdle = new Set();
  return vacated;
};

// The tables live at the last recompute, and whether the last of them then
// went away as the player exiting every table — as opposed to tables not being
// back yet after a page load.
let lastLiveGameIDs: string[] = [];
let exitedEveryTable = false;

const ensureBootRestore = (nowMs: number) => {
  if (bootRestore === null) {
    bootRestore = {
      gameIDs: readLastOpenTables(),
      until: nowMs + RESTORE_MAX_WAIT_MS,
      settledAt: nowMs + POST_LOAD_SETTLE_MS
    };
  }
  return bootRestore;
};

const currentOwner = (): string => {
  try {
    return localStorage.getItem('playerGUID') || '';
  } catch {
    return '';
  }
};

const readStoredSubscriptionOrder = (owner: string): string[] => {
  try {
    // An order stamped by a different member is not this player's. Unstamped
    // (written before the stamp existed, or by a guest) is accepted.
    const storedOwner = localStorage.getItem(SUBSCRIPTION_ORDER_OWNER_KEY) || '';
    if (storedOwner && storedOwner !== owner) return [];
    const raw = JSON.parse(localStorage.getItem(SUBSCRIPTION_ORDER_KEY) || '[]');
    return Array.isArray(raw) ? raw.map(String) : [];
  } catch {
    return [];
  }
};

const ensureSubscriptionOrder = (): string[] => {
  const owner = currentOwner();
  if (subscriptionOrder === null || owner !== subscriptionOrderOwner) {
    // A different player's restore snapshot is not this player's either.
    if (subscriptionOrder !== null) bootRestore = null;
    // Nor are the tables that player owned.
    ownTableByTournament = new Map();
    lastTrackedKeys = new Map();
    lastTileByTournamentKey = new Map();
    closedWhileIdle = new Set();
    lastLiveGameIDs = [];
    exitedEveryTable = false;
    subscriptionOrder = readStoredSubscriptionOrder(owner);
    subscriptionOrderOwner = owner;
  }
  return subscriptionOrder;
};

/**
 * Put new keys into the order. A key that left a vacancy takes it back; the
 * rest each take the leftmost vacancy, and any left over go to the front, in
 * the order given.
 */
const placeInOrder = (order: string[], newKeys: string[]): string[] => {
  const placed = [...order];
  // Returning tables first, so a new table can't take a returning one's slot.
  const unplaced = newKeys.filter((key) => {
    const own = placed.findIndex((id) => isVacant(id) && vacatedBy(id) === key);
    if (own === -1) return true;
    placed[own] = key;
    return false;
  });
  const front: string[] = [];
  for (const key of unplaced) {
    const vacancy = placed.findIndex(isVacant);
    if (vacancy === -1) front.push(key);
    else placed[vacancy] = key;
  }
  return front.length > 0 ? [...front, ...placed] : placed;
};

/**
 * A tournament table whose tournament isn't known yet: its row carries no
 * tournamentId and the player's details don't name it. Its gameID can't be
 * its order key: a rebalance destination belongs in its tournament's existing
 * slot, and giving it a vacancy under its gameID would use up a slot another
 * table left. It gets a slot once its tournament is known — from the details,
 * or its own payload — or at once under its gameID if its tournamentId is
 * known to be invalid. A loaded table waits only while some tournament slot
 * has no table (`tournamentSlotOpen`): otherwise it can't be a destination
 * (e.g. an observed table) and would never get a slot.
 */
const isAwaitingTournamentId = (
  t: ActiveTable,
  tables: Record<string, any>,
  gameKeyed: Set<string>,
  tournamentSlotOpen: boolean
): boolean =>
  isTournamentTabletype(t.tabletype) &&
  !t.tournamentId &&
  !gameKeyed.has(t.gameID) &&
  !hasInvalidTournamentId(tables?.[t.gameID]?.game?.tournamentId) &&
  (tournamentSlotOpen || !tables?.[t.gameID]?.game?.gameID);

// Tables whose tournamentId is known to be invalid, so they are keyed by
// gameID: from the player's tournament details, or from the playerGames row.
const collectGameKeyed = (playerGames: any[], fromDetails: Set<string>): Set<string> => {
  const gameKeyed = new Set(fromDetails);
  for (const g of playerGames || []) {
    if (g?.gameID && hasInvalidTournamentId(g.tournamentId)) gameKeyed.add(String(g.gameID));
  }
  return gameKeyed;
};

/**
 * The key a table is tracked under. Cash tables use their gameID; tournament
 * tables use their tournamentId, which stays the same when a rebalance moves
 * the player to a new gameID — so the table keeps its slot (EN-10857).
 */
export const orderKeyOf = (t: { gameID: string; tournamentId?: string }): string => {
  const tournamentId = canonicalTournamentId(t.tournamentId);
  return tournamentId ? tournamentOrderKey(tournamentId) : t.gameID;
};

/**
 * The key for a table from its own broadcast slice (state.tables[gameID]) and
 * the player's tournament details — for callers outside this hook that need a
 * table's key before it is listed.
 *
 * Null for a tournament table whose tournament isn't known yet: keyed by its
 * gameID it would take a slot another table left, when it may belong in its
 * tournament's existing one. Record nothing; the hook places it once its
 * tournament is known (see isAwaitingTournamentId).
 */
export const orderKeyOfSlice = (
  gameID: string,
  slice: any,
  playerTournamentDetails?: any[]
): string | null => {
  const tabletype = slice?.game?.tabletype;
  const raw = slice?.game?.tournamentId;
  const playing = readPlayingTournaments(playerTournamentDetails);
  // A missing or untyped slice doesn't make the table cash: when the details
  // assign the player to it, it is that tournament's table.
  if (!tabletype && playing.byGameID[gameID]) {
    return tournamentOrderKey(playing.byGameID[gameID]);
  }
  // Same sources as the hook: the slice's own id, else the player's details.
  const tournamentId = resolveTournamentId(tabletype, raw, playing.byGameID[gameID]);
  const gameKeyed = hasInvalidTournamentId(raw) || playing.gameKeyed.has(gameID);
  if (isTournamentTabletype(tabletype) && !tournamentId && !gameKeyed) return null;
  return orderKeyOf({ gameID, tournamentId });
};

// Callers pass an id already through canonicalTournamentId.
const tournamentOrderKey = (tournamentId: string): string => `tournament:${tournamentId}`;

type PlayingTournaments = { keys: Set<string>; byGameID: Record<string, string> };

// The player's own table of each tournament — the last one they were seated
// at, assigned to, or busted at — by tournamentId. It stays theirs, whatever
// the details say later (a re-entry's 'R' names no table), until another
// table of the tournament is. Rebuilt after a refresh from the details: a
// busted player's 'F' record still names the table. Reset for a new player.
let ownTableByTournament = new Map<string, string>();

/**
 * The key a listed table is tracked under. The player's own table of a
 * tournament is keyed by the tournament (orderKeyOf), so a rebalance or a
 * re-entry to a new table inherits its slot: seated there, assigned there,
 * the table they busted at (ownTableByTournament), or the one table listed
 * for a tournament they are playing (oneTablePerTournament) — e.g. the old
 * table standing in, unseated, while the destination loads. A table the
 * player only observes is keyed by its gameID: a busted or unregistered
 * player may observe several tables of one tournament (EN-9594), and each
 * needs its own slot, freed when it closes.
 */
export const trackedOrderKeyOf = (
  t: ActiveTable,
  playing: PlayingTournaments,
  ownTables: Map<string, string> = ownTableByTournament
): string => {
  const key = orderKeyOf(t);
  if (key === t.gameID) return key;
  const tournamentId = canonicalTournamentId(t.tournamentId);
  const isOwn =
    t.isSeated ||
    playing.byGameID[t.gameID] === tournamentId ||
    (tournamentId !== undefined && ownTables.get(tournamentId) === t.gameID) ||
    rebalanceDestinationOf(key) === t.gameID ||
    playing.keys.has(key);
  return isOwn ? key : t.gameID;
};

// Record the table each tournament's seat is at now: a table the player is
// seated at or assigned to. The table a busted ('F') record names only fills a
// tournament with no own table known yet — after a refresh — since the record
// can lag a re-entry whose new seat is already loaded.
const recordOwnTables = (
  tables: ActiveTable[],
  playing: PlayingTournaments & { bustedAt?: Record<string, string> }
): void => {
  const seats = new Map<string, string>();
  const busted = new Map<string, string>();
  for (const t of tables) {
    const tournamentId = canonicalTournamentId(t.tournamentId);
    if (!tournamentId) continue;
    if (t.isSeated || playing.byGameID[t.gameID] === tournamentId) {
      seats.set(tournamentId, t.gameID);
    } else if (playing.bustedAt?.[t.gameID] === tournamentId) {
      busted.set(tournamentId, t.gameID);
    }
  }
  busted.forEach((gameID, tournamentId) => {
    if (!seats.has(tournamentId) && !ownTableByTournament.has(tournamentId)) {
      ownTableByTournament.set(tournamentId, gameID);
    }
  });
  seats.forEach((gameID, tournamentId) => ownTableByTournament.set(tournamentId, gameID));
};

// Each listed table's key on the last recompute, by gameID. Tells which of
// several observed tables of a tournament was the player's own table.
let lastTrackedKeys = new Map<string, string>();

// The last tile listed under each tournament key: what the nav keeps showing
// in a tournament's slot while a rebalance moves the player off it.
let lastTileByTournamentKey = new Map<string, ActiveTable>();

/**
 * Stand-ins for tournaments mid-rebalance (EN-10857). The source table is
 * hidden as the move starts (marked exited, and in the grid removed) while the
 * destination is still loading, so the tournament's slot would show nothing
 * for that beat — while the grid keeps the source's cell. Each tournament
 * whose slot is protected and has no table listed shows its last tile instead,
 * pointing at the destination when the details name it, so a tap or the
 * active highlight follows the player's new seat. Gone once a table of the
 * tournament is listed or the protection lapses. Nav only: the grid and the
 * open-table count never see a stand-in.
 */
const rebalanceStandIns = (
  order: string[],
  listed: ActiveTable[],
  playing: PlayingTournaments,
  tables: Record<string, any>,
  nowMs: number
): Map<ActiveTable, string> => {
  const standIns = new Map<ActiveTable, string>();
  for (const key of order) {
    const last = lastTileByTournamentKey.get(key);
    if (!last || !isProtectedOrderKey(key, nowMs)) continue;
    // A rebalance only moves a player who is still in the tournament: one who
    // busted mid-move has no seat to be on the way to.
    if (!playing.keys.has(key)) continue;
    const tournamentId = canonicalTournamentId(last.tournamentId);
    const destination =
      rebalanceDestinationOf(key, nowMs) ??
      Object.keys(playing.byGameID).find((gid) => playing.byGameID[gid] === tournamentId);
    // Loaded, the destination shows as itself — or is left out deliberately
    // (e.g. listed under its gameID before its tournament is known).
    if (destination && tables?.[destination]?.game?.gameID) continue;
    const gameID = destination ?? last.gameID;
    const alreadyListed = listed.some(
      (t) => t.gameID === gameID || canonicalTournamentId(t.tournamentId) === tournamentId
    );
    // The source as the player last saw it, like the grid's held cell — but
    // nothing that can't carry over to the destination: no turn or vote to act
    // on, and not the source's shutdown (a closing table is what rebalances
    // its players, and the copy would come out disabled, stamped closing).
    const standIn = {
      ...last,
      id: gameID,
      gameID,
      isMyTurn: false,
      turnClock: null,
      voteClock: null,
      pendingShutdown: false
    };
    if (!alreadyListed) standIns.set(standIn, key);
  }
  return standIns;
};

// A table whose key changed is moved onto its new key at the same index: a
// tournament table first tracked under its gameID (before its tournamentId was
// known, or while observed) once it is the player's own, and the player's own
// table onto its gameID once they only observe it (busted there). An observed
// table takes the tournament's slot only while no own table of it holds it,
// and not from the table that held it last time.
const migrateToCanonicalOrderKeys = (
  order: string[],
  liveTables: ActiveTable[],
  keyOf: (t: ActiveTable) => string
): void => {
  const heldLast = (t: ActiveTable, key: string): boolean => lastTrackedKeys.get(t.gameID) === key;
  for (const t of liveTables) {
    const tournamentKey = orderKeyOf(t);
    if (tournamentKey === t.gameID) continue;
    if (keyOf(t) === tournamentKey) {
      const staleIdx = order.indexOf(t.gameID);
      if (staleIdx === -1) continue;
      // Already tracked under the tournament key — drop the duplicate.
      if (order.includes(tournamentKey)) order.splice(staleIdx, 1);
      else order[staleIdx] = tournamentKey;
      continue;
    }
    if (order.includes(t.gameID)) continue;
    const ownIdx = order.indexOf(tournamentKey);
    if (ownIdx === -1) continue;
    if (liveTables.some((other) => other !== t && keyOf(other) === tournamentKey)) continue;
    const heir = liveTables.find((other) => heldLast(other, tournamentKey));
    if (heir && heir !== t) continue;
    order[ownIdx] = t.gameID;
  }
};

// During a rebalance there is a moment when neither the old nor the new table
// is loaded. This keeps the tournament's slot from being pruned in that gap.
const TOURNAMENT_ORDER_PROTECT_MS = 20000;
const pendingTournamentOrderKeys = new Map<string, number>();
// The table each rebalance is moving the player to, when known, and when it
// was named. Outlives the slot protection: the protection ends once the new
// table is listed, but the lobby's details may still assign the seat being
// left, and the new table has to keep winning until they catch up. Lapses
// after TOURNAMENT_ORDER_PROTECT_MS, like the protection.
const rebalanceDestinations = new Map<string, { gameID: string; at: number }>();

const rebalanceDestinationOf = (key: string, nowMs: number = Date.now()): string | undefined => {
  const named = rebalanceDestinations.get(key);
  if (!named) return undefined;
  if (nowMs - named.at >= TOURNAMENT_ORDER_PROTECT_MS) {
    rebalanceDestinations.delete(key);
    return undefined;
  }
  return named.gameID;
};

// The destinations of the rebalances still in flight, tournamentId → gameID.
const activeRebalanceDestinations = (nowMs: number): Record<string, string> => {
  const out: Record<string, string> = {};
  Array.from(rebalanceDestinations.keys()).forEach((key) => {
    const gameID = rebalanceDestinationOf(key, nowMs);
    if (gameID) out[key.slice(tournamentOrderKey('').length)] = gameID;
  });
  return out;
};

const releaseProtection = (key: string): void => {
  pendingTournamentOrderKeys.delete(key);
};

// Tournaments the player is still playing (status 'P'), from their tournament
// details: the key of each (a game key when the tournamentId is invalid), the
// tournamentId of each assigned table, and the tables that fell back to a
// game key. Also the table each busted ('F') record names.
const readPlayingTournaments = (
  details: any[] | undefined
): PlayingTournaments & { gameKeyed: Set<string>; bustedAt: Record<string, string> } => {
  const keys = new Set<string>();
  const byGameID: Record<string, string> = {};
  const gameKeyed = new Set<string>();
  // The table each busted ('F') record names, gameID → tournamentId.
  const bustedAt: Record<string, string> = {};
  for (const d of details || []) {
    if (d?.status === 'F') {
      const tournamentId = canonicalTournamentId(d.tournamentId);
      if (tournamentId && Number(d.gameID) > 0) bustedAt[String(d.gameID)] = tournamentId;
      continue;
    }
    if (d?.status !== 'P') continue;
    const gameID = Number(d.gameID) > 0 ? String(d.gameID) : null;
    const tournamentId = canonicalTournamentId(d.tournamentId);
    if (tournamentId) {
      keys.add(tournamentOrderKey(tournamentId));
      if (gameID) byGameID[gameID] = tournamentId;
    } else if (gameID && hasInvalidTournamentId(d.tournamentId)) {
      keys.add(gameID);
      gameKeyed.add(gameID);
    }
  }
  return { keys, byGameID, gameKeyed, bustedAt };
};

/**
 * The tournaments the player is playing, less those a loaded table
 * contradicts: every table the details assign for it is loaded and shows the
 * player unseated. The details feed can lag a bust, leaving a stale 'P'; a
 * loaded table is the fresher signal (the same guard as
 * computeActiveTournamentRedirect). An assigned table not loaded yet can't
 * tell, so the details stand.
 */
const corroboratePlaying = <T extends PlayingTournaments>(
  playing: T,
  tables: Record<string, any> | undefined
): T => {
  const assignedByTournament = new Map<string, string[]>();
  Object.entries(playing.byGameID).forEach(([gameID, tournamentId]) => {
    assignedByTournament.set(tournamentId, [
      ...(assignedByTournament.get(tournamentId) || []),
      gameID
    ]);
  });
  const isLoadedUnseated = (gameID: string): boolean => {
    const slice = tables?.[gameID];
    return Boolean(slice?.game?.gameID) && Number(slice?.currentPlayer?.seatId ?? -1) <= 0;
  };
  const contradicted = Array.from(assignedByTournament)
    .filter(([, gameIDs]) => gameIDs.every(isLoadedUnseated))
    .map(([tournamentId]) => tournamentOrderKey(tournamentId));
  if (contradicted.length === 0) return playing;
  return {
    ...playing,
    keys: new Set(Array.from(playing.keys).filter((k) => !contradicted.includes(k)))
  };
};

/**
 * Protect a tournament's slot while a rebalance is in flight, moving the
 * player to `destinationGameID` (when the caller knows it).
 */
export const protectTournamentOrderKey = (
  tournamentId: string | number | null | undefined,
  destinationGameID?: string | number | null
): void => {
  const id = canonicalTournamentId(tournamentId);
  if (!id) return;
  const key = tournamentOrderKey(id);
  pendingTournamentOrderKeys.set(key, Date.now());
  if (destinationGameID) {
    rebalanceDestinations.set(key, { gameID: String(destinationGameID), at: Date.now() });
  } else {
    rebalanceDestinations.delete(key);
  }
};

const isProtectedOrderKey = (key: string, nowMs: number): boolean => {
  const at = pendingTournamentOrderKeys.get(key);
  if (at === undefined) return false;
  if (nowMs - at >= TOURNAMENT_ORDER_PROTECT_MS) {
    releaseProtection(key);
    return false;
  }
  return true;
};

// The next moment a slot protection lapses: the restore window (while a
// remembered table is still pending) or a rebalance protection. Null if none.
const nextOrderDeadline = (nowMs: number): number | null => {
  const deadlines = [
    ...Array.from(pendingTournamentOrderKeys.values(), (at) => at + TOURNAMENT_ORDER_PROTECT_MS),
    // A named destination stops outranking the details when it lapses.
    ...Array.from(rebalanceDestinations.values(), ({ at }) => at + TOURNAMENT_ORDER_PROTECT_MS)
  ];
  if (bootRestore && Object.keys(bootRestore.gameIDs).length > 0) {
    deadlines.push(bootRestore.until);
  }
  const future = deadlines.filter((at) => at > nowMs);
  return future.length > 0 ? Math.min(...future) : null;
};

/** Test-only: drop the shared order so suites start from a clean slate. */
export const __resetSubscriptionOrderForTests = (): void => {
  subscriptionOrder = null;
  subscriptionOrderOwner = '';
  lastWrittenOrder = '';
  bootRestore = null;
  closedWhileIdle = new Set();
  lastLiveGameIDs = [];
  exitedEveryTable = false;
  pendingTournamentOrderKeys.clear();
  rebalanceDestinations.clear();
  lastTrackedKeys = new Map();
  lastTileByTournamentKey = new Map();
  ownTableByTournament = new Map();
};

/** Test-only: the shared order, vacancies included. */
export const __peekTableOrderForTests = (): string[] =>
  ensureSubscriptionOrder().map((id) => (isVacant(id) ? VACANT_ORDER_SLOT : id));

/** Test-only: the key of the table that left each vacancy ('' elsewhere). */
export const __peekVacancyOwnersForTests = (): string[] =>
  ensureSubscriptionOrder().map((id) => (isVacant(id) ? vacatedBy(id) : ''));

const writeSubscriptionOrder = (order: string[]): void => {
  const serialized = JSON.stringify(order);
  const written = `${subscriptionOrderOwner}\n${serialized}`;
  if (written === lastWrittenOrder) return;
  try {
    localStorage.setItem(SUBSCRIPTION_ORDER_KEY, serialized);
    localStorage.setItem(SUBSCRIPTION_ORDER_OWNER_KEY, subscriptionOrderOwner);
    lastWrittenOrder = written;
  } catch {
    /* private mode / quota — keep the in-memory order only. */
  }
};

export interface UseActiveTablesResult {
  /** Player's open tables, in table order. */
  activeTables: ActiveTable[];
  /**
   * What the nav shows: `activeTables`, plus each table the player is queued on
   * whose table isn't loaded yet, in its own slot of the same order. Only nav
   * surfaces take this — the grid and the carousel render loaded tables only.
   */
  navTables: ActiveTable[];
  /**
   * Table slots the player is using: the open tables, plus each tournament
   * mid-rebalance whose table is between the source and the destination (its
   * nav stand-in). Sizes the grid; the add-table "+" hides at the larger of
   * this and heldSlots, so the beat a rebalance spends with neither table
   * loaded doesn't offer a slot that isn't free.
   */
  slotsInUse: number;
  /**
   * Table slots the cap counts: the strip's cards (tables, Sit & Go
   * registrations, waitlist cards — loaded or not) plus every open table
   * (countHeldTableSlots). What the add-table "+" hides at, so the strip
   * never offers a join the cap would refuse (EN-11131).
   */
  heldSlots: number;
  /**
   * Open a table and record it in the table order. Returns whether the open
   * was accepted (see ViewManagerContext.openTable): an action that must only
   * follow a real open — claiming a seat — gates on it.
   */
  openTable: (
    gameID: string,
    game: string,
    observe?: string,
    opts?: { forceSingle?: boolean; fromGesture?: boolean }
  ) => boolean;
}

/**
 * Record a newly opened table: it takes the leftmost vacancy if there is one,
 * otherwise the front. Already-tracked tables keep their slot.
 *
 * Call this from any flow that opens a table through ViewManagerContext
 * directly instead of useActiveTables().openTable. A table that arrives
 * without being opened is placed the same way when the order is recomputed.
 */
export const recordTableOpen = (gameID: string): void => {
  const order = ensureSubscriptionOrder();
  if (order.includes(gameID)) return;
  subscriptionOrder = placeInOrder(vacateClosedWhileIdle(order), [gameID]);
  writeSubscriptionOrder(subscriptionOrder);
};

export const useActiveTables = (): UseActiveTablesResult => {
  const playerGames = useAppSelector((state) => state.playerGames);
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const isMaintenance = useAppSelector(
    (state) => (state as any).maintenanceSchedule?.isMaintenance
  );
  // The player's waitlist spots. A queued table holds a slot in the order from
  // the moment the player joins, loaded or not, like an open table.
  const waitlistSpots = useAppSelector((state) => (state as any).waitlist?.mySpotByGameId) as
    | Record<string, WaitlistEntry>
    | undefined;
  // The same spots the cap counts (isQueuedSpot), so the two can't drift.
  const waitlisted = useMemo(
    () => Object.values(waitlistSpots || {}).filter(isQueuedSpot),
    [waitlistSpots]
  );
  const playerTournamentDetails = useAppSelector(
    (state) => (state as any).lobby?.playerTournamentDetails
  );
  const sitAndGoList = useAppSelector((state) => (state as any).lobby?.tournament?.sitAndGoList);
  const { openTable, restoreInFlight } = useContext(ViewManagerContext);

  // The engine keeps a player's tournament details on their current seat
  // through a rebalance, so they identify the new table before it loads and
  // hold its slot while the old table is torn down.
  const playingTournaments = useMemo(
    () => readPlayingTournaments(playerTournamentDetails),
    [playerTournamentDetails]
  );
  const gameKeyed = useMemo(
    () => collectGameKeyed(playerGames || [], playingTournaments.gameKeyed),
    [playerGames, playingTournaments]
  );

  // ── When each open vote window runs out, on this device ───────────────────
  //
  // The expiry in `adaptActiveTables` can only answer "the server says this is
  // over", and the case worth fixing is the one where the server stops saying
  // anything. So each window gets a deadline the moment it is first seen:
  // local now + whatever the payload said was still to run.
  //
  // Fixed ONCE per window and never recomputed from later frames. An earlier
  // attempt accumulated "time since the last payload" instead, which looked
  // equivalent and was not: `game.time` is whole seconds, several frames of a
  // busy table legitimately share one value, and the accumulator then counted a
  // perfectly healthy table as standing still and could withdraw a window with
  // time left on it. A deadline does not care how often frames arrive.
  //
  // `performance.now()` throughout, because it is monotonic — `Date.now()` moves
  // under an NTP correction or a sleeping laptop, and a jump either way would
  // move a deadline that is supposed to be fixed.
  //
  // ONE entry per table, holding the window's own server-side anchor
  // (`lastmove`). The anchor still says WHICH window the deadline belongs to, so
  // the next window on the same table gets its own rather than inheriting the
  // last one's — but it is a FIELD rather than part of the key, because a table
  // has only one window open at a time and the previous one is dead the moment a
  // new anchor is seen. Keyed by window, the map instead grew by one entry per
  // vote of the session and was trimmed only when the table closed — on a table
  // left open all night that is every window it ever ran (JRam review, #3590).
  const voteDeadlinesRef = useRef<Map<string, { anchor: string; at: number }>>(new Map());

  const voteWindowExpired = useCallback<VoteWindowExpired>((gameID, window) => {
    const anchor = String(window.anchor);
    const deadlines = voteDeadlinesRef.current;
    const now = performance.now();

    // Replaced when the anchor moves, never when the deadline passes: dropping
    // an expired entry would re-derive it from the same frozen payload on the
    // next render, answer 'not expired' again, and re-arm a window that has
    // already been withdrawn.
    let entry = deadlines.get(gameID);
    if (entry === undefined || entry.anchor !== anchor) {
      const remainingSecs = Math.max(0, window.totalSeconds - window.elapsedSeconds);
      entry = { anchor, at: now + remainingSecs * 1000 };
      deadlines.set(gameID, entry);
    }

    return now >= entry.at;
  }, []);

  useEffect(() => {
    // Drop deadlines for tables that are no longer open, so a re-opened table
    // cannot inherit one and withdraw a window that has only just started.
    const open = new Set(Object.keys(tables || {}).map(String));
    for (const gameID of Array.from(voteDeadlinesRef.current.keys())) {
      if (!open.has(gameID)) voteDeadlinesRef.current.delete(gameID);
    }
  }, [tables]);

  // ── A reason to re-render when one runs out ───────────────────────────────
  //
  // The expiry is derived, so it can only take effect on a render, and the case
  // it exists for is the one where no payload arrives to cause one.
  //
  // ONE timeout at the earliest deadline still in the future, not a poll. A
  // repeating interval gated on "some table's `runs` still carries a v" never
  // stopped: the frozen payload that makes the window expire keeps that marker
  // set forever, so the lobby re-derived itself every second for the rest of the
  // session — the timer outliving the thing it was watching for. A timeout fires
  // once, the withdrawal happens, the deadline is then in the past and nothing
  // reschedules.
  const [expiryTick, setExpiryTick] = useState(0);

  useEffect(() => {
    const now = performance.now();
    const future = Array.from(voteDeadlinesRef.current.values(), (d) => d.at).filter(
      (at) => at > now
    );
    if (future.length === 0) return undefined;

    // +1ms so the wake-up lands strictly past the deadline rather than on it,
    // which would re-derive to the same answer and schedule again.
    const id = setTimeout(() => setExpiryTick((n) => n + 1), Math.min(...future) - now + 1);
    return () => clearTimeout(id);
    // `expiryTick` re-enters this after each firing so a SECOND open window —
    // another table mid-vote — gets its own wake-up once the first has passed.
  }, [tables, expiryTick]);

  // The restore window and rebalance protections lapse on the clock, but only
  // take effect when the order is recomputed; a quiet lobby may broadcast
  // nothing to cause one. Wake once at the next deadline, like the vote expiry.
  const [orderDeadlineTick, setOrderDeadlineTick] = useState(0);
  // A just-exited table's mark lapses on the clock too, which changes what the
  // nav lists and what holds a slot with no store change; the cap reads the
  // marks live, so without this the strip and its "+" would lag behind it.
  const [exitTick, setExitTick] = useState(0);

  const tableLists = useMemo<{
    activeTables: ActiveTable[];
    navTables: ActiveTable[];
    slotsInUse: number;
  }>(() => {
    const nowMs = Date.now();
    const trackedOrder = ensureSubscriptionOrder();
    // Snapshot what was open before this page load on the first pass, before
    // the live set starts overwriting the stored record.
    const restore = ensureBootRestore(nowMs);
    const derived = adaptActiveTables(
      playerGames || [],
      tables || {},
      isExitedTable,
      voteWindowExpired,
      playingTournaments.byGameID
    );
    // Maintenance only blocks new joins, so keep the tables the player is
    // still seated at. One seat per tournament: the table the player is in.
    const playing = corroboratePlaying(playingTournaments, tables);
    // Mid-rebalance, the new table wins once it is loaded; until then the
    // old one stands in (or, once hidden, its stand-in — rebalanceStandIns).
    const next = oneTablePerTournament(
      isMaintenance ? derived.filter((t) => t.isSeated) : derived,
      playing.byGameID,
      playing.keys,
      activeRebalanceDestinations(nowMs)
    );
    recordOwnTables(next, playing);
    const keyOf = (t: ActiveTable): string => trackedOrderKeyOf(t, playing);
    migrateToCanonicalOrderKeys(trackedOrder, next, keyOf);
    lastTrackedKeys = new Map(next.map((t) => [t.gameID, keyOf(t)]));
    const currentIds = new Set(next.map(keyOf));
    // A loaded table no longer needs rebalance or restore protection. The
    // restore record is keyed by gameID, even for a tournament table. A
    // rebalance with a named destination is over only once that table is
    // listed: the source shares the tournament's key, and a render can land
    // between arming the protection and hiding the source.
    next.forEach((t) => {
      const key = keyOf(t);
      const destination = rebalanceDestinationOf(key, nowMs);
      if (destination === undefined || destination === String(t.gameID)) releaseProtection(key);
    });
    next.forEach((t) => delete restore.gameIDs[String(t.gameID)]);
    const restoring = nowMs < restore.until;
    // A remembered tournament table is recorded by gameID but tracked under
    // its tournament key, and which tournament it belongs to isn't known until
    // it loads or the player's details arrive. While any remembered table is
    // not tracked by its gameID, every tournament slot is held.
    const tournamentRestorePending =
      restoring && Object.keys(restore.gameIDs).some((gid) => !trackedOrder.includes(gid));
    // Turn closed tables into vacancies. Skip while nothing is loaded or a
    // refresh restore is running: tables come back one at a time, and pruning
    // then would shuffle the order. Tables still on their way back from
    // before the refresh, and tournaments the player is still in
    // (mid-rebalance), keep their slots.
    const shouldPrune = next.length > 0 && !restoreInFlight;
    // A queued table that is loaded is placed as the live table it is (its own
    // key, held back if its tournament isn't known). Only an unloaded one is
    // placed by its spot — waitlists are cash tables, keyed by gameID. Matched
    // by the slot id the cap counts it under (queuedSlotIdOf): a spot sharing
    // its gameID with a live table of the other game is a table of its own.
    const liveGameIDs = new Set(next.map((t) => String(t.gameID)));
    const unloadedWaitlist = waitlisted.filter((e) => !liveGameIDs.has(queuedSlotIdOf(e, tables)));
    const waitlistKeys = new Set(unloadedWaitlist.map((e) => String(e.gameId)));
    const isStillOpen = (id: string): boolean =>
      isVacant(id) ||
      currentIds.has(id) ||
      waitlistKeys.has(id) ||
      (restoring && restore.gameIDs[id] !== undefined) ||
      (tournamentRestorePending && id.startsWith(tournamentOrderKey(''))) ||
      playing.keys.has(id) ||
      isProtectedOrderKey(id, nowMs);
    const pruned = shouldPrune
      ? trackedOrder.map((id) => (isStillOpen(id) ? id : vacate(id)))
      : [...trackedOrder];
    if (next.length > 0) exitedEveryTable = false;
    else if (lastLiveGameIDs.length > 0 && !restoreInFlight) {
      // Once the page has settled, the last table going away is an exit. Before
      // then it can be the page load itself (tables dropping out while they
      // resubscribe), so it counts only when the player exited each of them
      // themselves — a record only a delivered Exit Table leaves.
      const settled = nowMs >= restore.settledAt;
      if (settled || lastLiveGameIDs.every((gid) => isPlayerExitedTable(gid))) {
        exitedEveryTable = true;
      }
    }
    lastLiveGameIDs = next.map((t) => String(t.gameID));
    const idle = next.length === 0 && !restoreInFlight;
    // The player has exited every table: the last live one went away (see
    // above), and nothing is on its way back, queued for, or a tournament still
    // in play. The order starts over rather than holding places for tables
    // that are gone.
    if (idle && exitedEveryTable && trackedOrder.every((id) => isVacant(id) || !isStillOpen(id))) {
      subscriptionOrder = [];
      closedWhileIdle = new Set();
      exitedEveryTable = false;
      return { activeTables: next, navTables: next, slotsInUse: next.length };
    }
    closedWhileIdle = idle ? new Set(trackedOrder.filter((id) => !isStillOpen(id))) : new Set();
    // Open tables not yet tracked (auto-resubscribed, seated by the engine)
    // fill vacancies the same way an explicit open does — except a tournament
    // table not yet known to belong to a tournament, which may be a rebalance
    // destination that already owns a slot.
    const tournamentSlotOpen = pruned.some(
      (id) => id.startsWith(tournamentOrderKey('')) && !currentIds.has(id)
    );
    // One entry per key: the player's seat and a table of its tournament still
    // lingering after a rebalance share the tournament's slot.
    const untracked = Array.from(
      new Set(
        next
          .filter((t) => !isAwaitingTournamentId(t, tables, gameKeyed, tournamentSlotOpen))
          .map(keyOf)
      )
    ).filter((id) => !pruned.includes(id));
    // Waitlist spots not yet in the order take a slot the same way, loaded or
    // not — joining a waitlist places the table like opening it.
    const untrackedWaitlist = Array.from(waitlistKeys).filter(
      (id) => !pruned.includes(id) && !untracked.includes(id)
    );
    const newKeys = [...untracked, ...untrackedWaitlist];
    // With nothing loaded, a waitlist join is the only way a new table arrives
    // here, and it is placed like an explicit open: into the slots the tables
    // that closed while idle left, not ahead of them.
    const order = placeInOrder(
      newKeys.length > 0 ? vacateClosedWhileIdle(pruned) : pruned,
      newKeys
    );
    subscriptionOrder = order;
    // Persisted in the effect below, not during render. A table held back
    // above shows last until its tournament is known.
    const listed = arrangeInTableOrder(next, order, keyOf, 'last');
    listed.forEach((t) => {
      const key = keyOf(t);
      if (key.startsWith(tournamentOrderKey(''))) lastTileByTournamentKey.set(key, t);
    });
    const placeholders = unloadedWaitlist.map((e) =>
      waitlistPlaceholder(e, queuedSlotIdOf(e, tables))
    );
    const standIns = rebalanceStandIns(order, listed, playing, tables, nowMs);
    const navKeyOf = (t: ActiveTable): string => standIns.get(t) ?? keyOf(t);
    const navExtras = [...placeholders, ...Array.from(standIns.keys())];
    return {
      activeTables: listed,
      navTables: navExtras.length
        ? arrangeInTableOrder([...next, ...navExtras], order, navKeyOf, 'last')
        : listed,
      slotsInUse: listed.length + standIns.size
    };
    // `expiryTick` is a dependency with no use in the body, deliberately: it is
    // the re-render that lets the vote-window expiry above take effect when no
    // payload arrives to cause one. `voteWindowExpired` is stable (useCallback,
    // no deps) and reads the ref at call time. `orderDeadlineTick` is the same,
    // for the slot protections' deadlines, and `exitTick` for the exit marks'.
  }, [
    playerGames,
    tables,
    isMaintenance,
    restoreInFlight,
    playingTournaments,
    gameKeyed,
    waitlisted,
    voteWindowExpired,
    expiryTick,
    orderDeadlineTick,
    exitTick
  ]);
  const { activeTables } = tableLists;

  // Sit & Gos the player is registered for that haven't started: each holds a
  // table slot, so each without a card that holds one already (matched by
  // gameID or tournament) gets one in the nav (EN-11131). The same accounting
  // the table cap uses (countHeldTableSlots), so the two can't disagree about
  // which ones hold a slot — or about how many slots are held. They count in
  // heldSlots, not slotsInUse: slotsInUse sizes the grid, and a registration
  // has no cell.
  const { navTables, slotsInUse, shownSlots, heldSlots } = useMemo(() => {
    const usage = countUsedTableSlots(tables, sitAndGoList, playerTournamentDetails, isExitedTable);
    // Each queued table is a card: an unloaded one as its placeholder, a
    // loaded one as the live table (same gameID, one card). Its spot holds the
    // slot even when the strip doesn't list the table itself (e.g. the player
    // exited it but is still queued), so it is passed as a queued card.
    const queued = queuedGameIdsOf(waitlistSpots, tables);
    const { unshownSngs, total } = countHeldTableSlots(
      shownTableSlotsOf(tableLists.navTables, queued),
      usage,
      isExitedTable
    );
    const registrations = unshownSngs.map((sng) => sngRegistrationPlaceholder(sng, sitAndGoList));
    const nav = registrations.length
      ? [...tableLists.navTables, ...registrations]
      : tableLists.navTables;
    // A registration's card only shows the slot the count above already holds
    // for it (as pending), so adding the cards leaves the total unchanged.
    return {
      navTables: nav,
      slotsInUse: tableLists.slotsInUse,
      shownSlots: shownTableSlotsOf(nav, queued),
      heldSlots: total
    };
    // Re-derived with tableLists, which `exitTick` recomputes when an exit mark
    // lapses, so the "+" and the cap (which reads the marks live) agree.
  }, [tableLists, tables, sitAndGoList, playerTournamentDetails, waitlistSpots]);

  // Read through refs so openTable keeps one identity across broadcasts.
  const activeTablesRef = useRef(activeTables);
  activeTablesRef.current = activeTables;
  const navTablesRef = useRef(navTables);
  navTablesRef.current = navTables;
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  const playerTournamentDetailsRef = useRef(playerTournamentDetails);
  playerTournamentDetailsRef.current = playerTournamentDetails;

  const handleOpenTable = useCallback(
    (
      gameID: string,
      game: string,
      observe = 'no',
      opts?: { forceSingle?: boolean; fromGesture?: boolean }
    ): boolean => {
      // A registration's card whose table isn't known yet has nothing to open:
      // its gameID is a registration slot id (sng:<tournamentId>), not a table.
      const card = navTablesRef.current.find((t) => String(t.gameID) === String(gameID));
      if (card?.notOpenable) return false;
      // Record only an open that was accepted — a rejected one must not use
      // up a vacancy or add a phantom entry.
      const accepted = openTable(gameID, game, observe, opts);
      if (!accepted) return false;
      // Use the tracked key for a table that's already open, so switching to
      // an open tournament table isn't recorded as a new open. A table not
      // loaded yet is keyed from its slice and the player's details, so a
      // re-entered tournament's new table takes back its tournament's slot
      // rather than the leftmost vacancy; one whose tournament isn't known yet
      // is placed by the hook once it is. A table opened to observe is keyed
      // by its gameID, as the hook keys it (see trackedOrderKeyOf).
      const known = activeTablesRef.current.find((t) => String(t.gameID) === String(gameID));
      const key = known
        ? trackedOrderKeyOf(
            known,
            corroboratePlaying(
              readPlayingTournaments(playerTournamentDetailsRef.current),
              tablesRef.current
            )
          )
        : observe === 'yes'
        ? String(gameID)
        : orderKeyOfSlice(
            String(gameID),
            tablesRef.current?.[String(gameID)],
            playerTournamentDetailsRef.current
          );
      if (key) recordTableOpen(key);
      return true;
    },
    [openTable]
  );

  // Persist the order (vacancies included) whenever it is recomputed.
  useEffect(() => {
    if (subscriptionOrder) {
      writeSubscriptionOrder(subscriptionOrder);
    }
  }, [activeTables]);

  // Tell the table cap what the strip shows: its tables, its Sit & Go
  // registrations, and a card for each queued table (YourTables renders a spot
  // whose table isn't listed as a card of its own). The cap counts this plus
  // every open table, with the exit marks read at the moment of the join
  // (countHeldTableSlots) (EN-11131). Published under this instance, and
  // withdrawn when it unmounts, so the cap never reads a strip that is gone.
  // With it, the count the "+" hides at, which the Observe button reads too
  // (useStripSlotsInUse).
  const shownSlotsOwner = useRef(Symbol('useActiveTables')).current;
  const slotsForAddTable = addTableSlotsInUse(slotsInUse, heldSlots);
  useEffect(() => {
    publishShownTableSlots(shownSlotsOwner, shownSlots, slotsForAddTable);
  }, [shownSlotsOwner, shownSlots, slotsForAddTable]);
  useEffect(() => () => withdrawShownTableSlots(shownSlotsOwner), [shownSlotsOwner]);

  // Rescheduled on every recompute, which is also when the deadlines change.
  // +1ms so the recompute lands strictly past the deadline.
  useEffect(() => {
    const now = Date.now();
    const at = nextOrderDeadline(now);
    if (at === null) return undefined;
    const id = setTimeout(() => setOrderDeadlineTick((n) => n + 1), at - now + 1);
    return () => clearTimeout(id);
  }, [activeTables]);
  useEffect(() => {
    const now = Date.now();
    const at = nextRecentlyExitedExpiry(now);
    if (at === null) return undefined;
    const id = setTimeout(() => setExitTick((n) => n + 1), at - now + 1);
    return () => clearTimeout(id);
  }, [activeTables, tables, exitTick]);

  return { activeTables, navTables, slotsInUse, heldSlots, openTable: handleOpenTable };
};

export default useActiveTables;
